import React, { useContext, createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import useNotifications from 'hooks/useNotifications.js';
import localPersistenceHelper from 'helpers/localPersistenceHelper.js';
import * as ROUTES from 'constants/routes.js';
import { buildRoute } from 'constants/routes.js';
import { useLocalization } from './LocalizationContext.jsx';
import useDubbingJobs from 'pages/Show/DownloadDrawer/hooks/useDubbingJobs.js';
import { useOrgUser } from './OrgUserContext.jsx';

const defaultValue = {};
const storage = localPersistenceHelper.storages.LOCAL_STORAGE;

export const OngoingDubsContext = createContext(defaultValue);

export const OngoingDubsContextProvider = (props) => {
  const [dubJobs, setDubJobs] = useState(localPersistenceHelper.getItem({ key: 'dubJobs', storage }) || []);
  const { handleFooterNotification } = useNotifications();
  const history = useHistory();
  const { t } = useLocalization();
  const { isSignedIn } = useOrgUser();

  const jobsAvailableForBroadcasts = useMemo(
    () =>
      dubJobs.reduce((acc, job) => {
        if (!acc.includes(job.broadcastId)) acc.push({ id: job.broadcastId, hasJobs: true });
        return acc;
      }, []),
    [dubJobs],
  );

  const addDubJob = useCallback(
    (job) => setDubJobs((prevJobs) => {
      const jobAlreadyExists = prevJobs.some((j) => j.jid === job.jid);
      const dubs = jobAlreadyExists
        ? prevJobs
        : [...prevJobs, job];

      localPersistenceHelper.setItem({ key: 'dubJobs', data: dubs, storage });
      return dubs;
    }),
    [],
  );

  const handleShowNotification = useCallback(
    (job, status) => {
      const { orgId, eventId: showId } = job;
      const showLink = showId && buildRoute(ROUTES.ORG_SHOW, { orgId, showId });
      const isOnShowPage = window.location?.href?.includes(showLink);
      // Only show notifications for these two known states
      if (status === 'failed') {
        // Show a notification to the user that the dubbing job has been processed
        handleFooterNotification({
          type: 'error',
          title: t('downloadDrawer.dubbingVideoFailedProcessedTitle'),
          message: t('downloadDrawer.dubbingVideoFailedProcessedMessage'),
          // If we're not on the show page, we should show an
          // action button to let the user go to the show page.
          ...(!isOnShowPage && {
            autoHide: false,
            action: () => history.push(showLink),
            actionText: t('genericLabels.goToShow'),
          }),
        });
      }
    },
    [history, handleFooterNotification, t],
  );

  const removeDubJob = useCallback(
    (jid, status) => {
      const job = dubJobs.find((job) => job.jid === jid);
      if (!job) return;

      handleShowNotification(job, status);

      // Remove the job from the ongoing dubs context
      setDubJobs((prevJobs) => {
        const dubs = prevJobs.filter((job) => job.jid !== jid);
        localPersistenceHelper.setItem({ key: 'dubJobs', data: dubs, storage });
        return dubs;
      });
    },
    [dubJobs, handleShowNotification],
  );

  useEffect(() => {
    if (!isSignedIn) {
      setDubJobs(() => {
        localPersistenceHelper.removeItem({ key: 'dubJobs', storage });
        return [];
      });
    }
  }, [isSignedIn]);

  const context = {
    addDubJob,
    removeDubJob,
    dubJobs,
  };

  useDubbingJobs({
    broadcasts: jobsAvailableForBroadcasts,
    onError: () => {},
    pollIntervalMs: 70000,
    context,
  });

  return <OngoingDubsContext.Provider value={context}>{props.children}</OngoingDubsContext.Provider>;
};

export const useOngoingDubsContext = () => useContext(OngoingDubsContext);
